import {colors} from '@/styles/tokens';
import {cdnImage, cdnUrl} from '@/util/cdnImage';
import {meetingLink} from '@/constants';

const iso27001 = cdnUrl('Saas-developement-company/ISO-27001-Aligned-Architecture.webp');
const awsAdvanced = cdnUrl('Saas-developement-company/AWS-Advanced-partner-logo.webp');
const microsoftGoldPartner = cdnUrl('Saas-developement-company/Microsoft-Gold -partner-logo.webp');
const googleCloudPartner = cdnUrl('Saas-developement-company/Google-Cloud-partner-logo.webp');

export const saasBannerBottom = [
    {src: iso27001, alt: 'ISO 27001 Aligned Architecture', width: 160, height: 60},
    {src: awsAdvanced, alt: 'AWS Advanced Partner', width: 160, height: 60},
    {src: microsoftGoldPartner, alt: 'Microsoft Gold Partner', width: 160, height: 60},
    {src: googleCloudPartner, alt: 'Google Cloud Partner', width: 160, height: 60}
];

const statProjects = cdnImage('web3/Web3-Stats-Implementations.svg');
const statEngagements = cdnImage('web3/Web3-Stats-Engagements.svg');
const statEngineers = cdnImage('web3/Web3-Stats-Engineers.svg');
const statPlatforms = cdnImage('web3/Web3-Stats-BlockchainPlatforms.svg');

export const saasSocialProofData = {
    sectionLabel: 'Trusted by builders behind',
    clientNames: ['iFinca', 'Cimet', 'IntegraLedger', 'P2B', 'Friendspire', 'TEM', 'Aithentic'],
    narrativeText:
        'From early-stage SaaS startups validating product-market fit to enterprise digital transformation programs for Fortune 500 procurement teams — Debut Infotech is the SaaS development company that engineering leaders and founders trust when architecture decisions, compliance requirements, and delivery timelines all converge on a single engagement.',
    statCards: [
        {
            value: '100+',
            color: colors.primary.main,
            label: 'SaaS products and platforms delivered across B2B, B2C, and enterprise verticals',
            icon: statProjects,
            iconH: 18
        },
        {
            value: '15',
            color: colors.accent.orange,
            label: 'Years delivering SaaS application development services across cloud-native stacks',
            icon: statEngagements,
            iconH: 20
        },
        {
            value: '20+',
            color: colors.accent.orange,
            label: 'Countries and regulatory jurisdictions served with compliant SaaS delivery',
            icon: statEngineers,
            iconH: 18
        },
        {
            value: '94%',
            color: colors.primary.main,
            label: 'Client retention rate — SaaS partnerships that extend well past initial launch',
            icon: statPlatforms,
            iconH: 12
        }
    ]
};

const growthIcon1 = cdnUrl('Saas-developement-company/global-software-market.svg');
const growthIcon2 = cdnUrl('Saas-developement-company/global-it-spending.svg');
const growthIcon3 = cdnUrl('Saas-developement-company/custom-saas-market-growth.svg');
const growthIcon4 = cdnUrl('Saas-developement-company/ai-integrated-saas-adoption.svg');

export const saasGrowthData = {
    children: [
        {
            src: growthIcon1,
            redirect: 'https://www.grandviewresearch.com/press-release/global-software-market',
            heading: '$1.39T Global Software Market Projected by 2030',
            content: [
                'The global software market is forecast to reach $1,397.31 billion by 2030, growing at a CAGR of 11.3% from 2025 to 2030. Enterprise SaaS adoption, cloud migration, and subscription-model standardization are the primary growth drivers across every vertical — creating a structural demand for SaaS product development companies with proven delivery records.'
            ]
        },
        {
            src: growthIcon2,
            redirect:
                'https://www.gartner.com/en/newsroom/press-releases/2025-10-22-gartner-forecasts-worldwide-it-spending-to-grow-9-point-8-percent-in-2026-exceeding-6-trillion-dollars-for-the-first-time',
            heading: '$6.08T Global IT Spending Forecast for 2026',
            content: [
                'Worldwide IT spending is projected to reach $6.08 trillion in 2026, growing at 9.8% year-over-year — with AI-integrated SaaS infrastructure accounting for a disproportionate share of new investment decisions. Organizations not building AI-native SaaS platforms today are building platforms they will need to rebuild within five years.'
            ]
        },
        {
            src: growthIcon3,
            redirect: 'https://www.grandviewresearch.com/industry-analysis/custom-software-development-market-report',
            heading: '22.6% CAGR in Custom SaaS Development Demand',
            content: [
                'The global custom software development market — where custom SaaS development is the fastest-growing segment — is projected to grow from $43.16 billion in 2024 to $146.18 billion by 2030 at a CAGR of 22.6%. This growth reflects businesses replacing generic SaaS subscriptions with purpose-built platforms they own and control.'
            ]
        },
        {
            src: growthIcon4,
            redirect: 'https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai',
            heading: '62% Enterprises Scaling AI-Integrated SaaS Workflows',
            content: [
                'Around 62% of enterprises are actively experimenting with or scaling AI agents embedded in SaaS platforms — autonomous systems executing compliance automation, customer engagement, demand forecasting, and decision-making workflows. AI in SaaS development is no longer a competitive advantage. It is a procurement expectation.'
            ]
        }
    ]
};

const iconOurFix = cdnImage('web3/Web3-Problem-OurFix.svg');

export const saasProblemCardsData = {
    ourFixIcon: iconOurFix,
    cards: [
        {
            icon: cdnUrl('Saas-developement-company/saas-compliance-retrofit.webp'),
            iconSize: 26,
            title: 'The Compliance Retrofit Crisis',
            challenge: `Adding HIPAA, SOC 2, or GDPR requirements after deployment costs 4–8× more than embedding them at the architecture stage. Regulators don't accept "we'll fix it later." We map every compliance requirement in Week 1 — before a line of code is written.`,
            fix: ' With Debut Infotech: Compliance embedded in data models, access controls, and audit trails.'
        },
        {
            icon: cdnUrl('Saas-developement-company/multi-tenancy-architecture-complexity.svg'),
            iconSize: 26,
            title: 'The Multi-Tenancy Afterthought',
            challenge: `A SaaS platform rebuilt for multi-tenancy 18 months post-launch is not a retrofit — it's a rebuild. Tenant isolation, data separation, and shared resource logic must be base requirements on Day 1, not features added when your second enterprise client demands them.`,
            fix: 'With Debut Infotech: Multi-tenant SaaS architecture designed at the system design stage — no exceptions.'
        },
        {
            icon: cdnUrl('Saas-developement-company/saas-cost-spiral.webp'),
            iconSize: 24,
            title: 'The SaaS Cost Spiral',
            challenge: `$50K/year across tools that partially fit. By Year 3 you've spent $150K+ on software you don't own, can't customize, and lose when you cancel. A purpose-built custom SaaS platform with full IP ownership pays back within 18–36 months.`,
            fix: 'With Debut Infotech: Full IP ownership, purpose-built for your business logic — not a generic SaaS wrapper.'
        },
        {
            icon: cdnUrl('Saas-developement-company/mvp-to-scale.webp'),
            iconSize: 24,
            title: 'The MVP-to-Scale Collapse',
            challenge: `An MVP built for speed collapses under Series B scrutiny. Investors perform technical due diligence. The SaaS architecture that worked at 1,000 users fails visibly at 50,000. We build production-quality architecture from the MVP sprint — documented, extensible, audit-ready.`,
            fix: 'With Debut Infotech: SaaS MVP development built for scale from sprint 1 — not rebuilt after funding.'
        }
    ]
};
export const saasSolutionsData = [
    {
        title: 'Food Delivery & On-Demand SaaS',
        description:
            'Platforms connecting restaurants, delivery fleets, and end consumers in real time — with dynamic order routing, live tracking, surge pricing, and multi-restaurant management built for scale from day one.',
        points: [
            'Real-time order matching, dispatch, and delivery tracking',
            'Multi-restaurant management and menu configuration',
            'Dynamic pricing, surge management, and driver incentive logic',
            'Customer app, merchant dashboard, and operations portal'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/food-delivery-on-demand-saas-platform.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Food Delivery SaaS',
        useCaseLink: ''
    },
    {
        title: 'HR & People Management SaaS',
        description:
            'End-to-end workforce platforms replacing fragmented HR toolstacks — applicant tracking, onboarding, performance management, payroll integration, and workforce analytics in a single tenanted system.',
        points: [
            'Applicant tracking, onboarding workflows, and offer management',
            'Performance cycles, OKRs, and continuous feedback systems',
            'Payroll integration, benefits administration, and compliance reporting',
            'Workforce analytics and predictive attrition modeling'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/hr-people-management-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your HR SaaS',
        useCaseLink: ''
    },
    {
        title: 'Project Management & Collaboration SaaS',
        description:
            'Vertical-specific project management for construction, legal, healthcare, and agency operations — where horizontal tools like Asana and Notion fall short on domain logic, compliance requirements, and industry-specific workflows.',
        points: [
            'Task management, sprint planning, and milestone tracking',
            'Real-time collaboration, document co-editing, and version control',
            'Time tracking, resource allocation, and capacity planning',
            'Client portal and external stakeholder access management'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/project-management-collaboration-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your PM SaaS',
        useCaseLink: ''
    },
    {
        title: 'Learning Management SaaS (LMS)',
        description:
            'Online learning platforms for corporate training, academic institutions, and EdTech companies — with adaptive content delivery, assessment engines, certification management, and learner analytics.',
        points: [
            'Course creation, SCORM/xAPI content hosting, and delivery',
            'Adaptive learning paths and AI-driven content recommendations',
            'Assessment, certification, and credentialing workflows',
            'Learner analytics, completion reporting, and engagement dashboards'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/learning-management-lms-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your LMS Platform',
        useCaseLink: ''
    },
    {
        title: 'Booking & Scheduling SaaS',
        description:
            'Appointment, reservation, and scheduling platforms for healthcare, hospitality, fitness, professional services, and field operations — with calendar sync, automated reminders, and payment collection built in.',
        points: [
            'Multi-provider scheduling with availability and conflict management',
            'Automated reminders, confirmations, and no-show handling',
            'Payment collection, deposits, and cancellation policy enforcement',
            'Customer self-service portal and business operations dashboard'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/booking-scheduling-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Booking SaaS',
        useCaseLink: ''
    },
    {
        title: 'CRM & Sales Automation SaaS',
        description:
            'Customer relationship and sales pipeline platforms purpose-built for industries where Salesforce is overkill — with automation, deal tracking, revenue intelligence, and deep integration into existing communication stacks.',
        points: [
            'Contact management, pipeline stages, and deal tracking',
            'Sales automation, follow-up sequences, and activity logging',
            'Revenue forecasting, quota tracking, and team performance analytics',
            'Email, calendar, and communication tool integrations'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/crm-sales-automation-saas-platform.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your CRM SaaS',
        useCaseLink: ''
    },
    {
        title: 'eCommerce & Retail SaaS',
        description:
            'Multi-channel commerce platforms for DTC brands and retail operators — inventory management, order orchestration, AI-powered personalization, and subscription commerce on headless architecture.',
        points: [
            'Multi-channel inventory, catalog, and order management',
            'AI-driven product recommendations and dynamic pricing',
            'Subscription commerce, loyalty programs, and gift cards',
            'Headless storefront with CMS and composable integrations'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/ecommerce-retail-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your eCommerce SaaS',
        useCaseLink: ''
    },
    {
        title: 'Telemedicine & Digital Health SaaS',
        description:
            'HIPAA-compliant virtual care platforms — video consultations, patient engagement, remote monitoring, and EHR integration for healthcare providers entering digital-first care delivery.',
        points: [
            'HIPAA-compliant video consultation and secure messaging',
            'Patient intake, consent management, and appointment scheduling',
            'EHR integration and clinical documentation workflows',
            'Remote monitoring data ingestion and care team dashboards'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/telemedicine-digital-health-saas-platform.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your HealthTech SaaS',
        useCaseLink: ''
    },
    {
        title: 'AI Copilot & Productivity SaaS',
        description:
            'The most funded SaaS category of 2024–2025. Intelligent assistants embedded in existing workflows — writing, research, document analysis, and decision support powered by LLMs trained on domain-specific data.',
        points: [
            'LLM integration with RAG pipelines for domain-specific accuracy',
            'Context-aware suggestions embedded inside existing product UI',
            'Document analysis, summarization, and extraction workflows',
            'Usage analytics, feedback loops, and model performance monitoring'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/ai-copilot-productivity-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your AI Copilot SaaS',
        useCaseLink: ''
    },
    {
        title: 'Fleet & Field Operations SaaS',
        description:
            'Real-time operations platforms for logistics companies, field service businesses, and transportation operators — live vehicle tracking, route optimization, maintenance scheduling, and driver management.',
        points: [
            'Real-time GPS tracking, geofencing, and live dispatch',
            'AI-driven route optimization and fuel efficiency analytics',
            'Preventive maintenance scheduling and asset lifecycle management',
            'Driver performance monitoring and compliance reporting'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/fleet-field-operations-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Fleet SaaS',
        useCaseLink: ''
    },
    {
        title: 'FinTech & Payments SaaS',
        description:
            'Financial platforms for lenders, payment operators, and wealth managers — loan origination, payment processing, KYC/KYB automation, and portfolio analytics under PCI-DSS, SOC 2, and regional frameworks.',
        points: [
            'Loan origination, underwriting workflows, and credit decisioning',
            'Payment processing, multi-currency support, and reconciliation',
            '<a href="/blog/blockchain-kyc" target="_blank">KYC</a>/KYB automation and <a href="/blog/anti-money-laundering-aml-in-cryptocurrency" target="_blank">AML</a> transaction monitoring',
            'Portfolio analytics, reporting, and investor dashboards'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/fintech-payments-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your FinTech SaaS',
        useCaseLink: ''
    },
    {
        title: 'Real Estate & PropTech SaaS',
        description:
            'Property management, investment analytics, and tenant experience platforms — lease management, maintenance workflows, AI-driven rent optimization, and digital transaction management for operators and investors.',
        points: [
            'Property and lease management with tenant self-service portal',
            'Maintenance request tracking and vendor management',
            'AI-driven rent pricing optimization and occupancy forecasting',
            'Investment portfolio analytics and digital closing workflows'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/real-estate-proptech-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your PropTech SaaS',
        useCaseLink: ''
    },
    {
        title: 'Social & Community SaaS',
        description:
            'Community-led platforms, creator tools, and social networking SaaS — niche social networks, membership communities, creator monetization platforms, and professional networking tools for specific audience segments.',
        points: [
            'User profiles, follow graphs, and activity feed architecture',
            'Content publishing, moderation, and community governance tools',
            'Creator monetization — subscriptions, tipping, and paywall logic',
            'Engagement analytics, growth metrics, and retention dashboards'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/social-community-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Community SaaS',
        useCaseLink: ''
    },
    {
        title: 'Event & Ticketing SaaS',
        description:
            'End-to-end event management platforms for organizers, venues, and enterprises — ticket sales, attendee management, virtual/hybrid event delivery, sponsorship management, and post-event analytics.',
        points: [
            'Event creation, ticketing, and seat map management',
            'Attendee registration, check-in, and badge management',
            'Virtual and hybrid event delivery with live streaming integration',
            'Sponsorship management, analytics, and post-event reporting'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/event-ticketing-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Event SaaS',
        useCaseLink: ''
    },
    {
        title: 'Restaurant & Hospitality SaaS',
        description:
            'Operations platforms for restaurants, hotel chains, and hospitality groups — POS integration, reservation management, inventory control, staff scheduling, and guest experience management in one system.',
        points: [
            'POS integration, table management, and order routing',
            'Reservation management, waitlist, and guest communication',
            'Inventory tracking, supplier management, and waste reduction',
            'Staff scheduling, tip management, and labor cost analytics'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/restaurant-hospitality-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Hospitality SaaS',
        useCaseLink: ''
    },
    {
        title: 'Fitness & Wellness SaaS',
        description:
            'Membership management, class scheduling, and digital coaching platforms for gyms, studios, personal trainers, and wellness brands — with wearable integration, progress tracking, and community features.',
        points: [
            'Membership management, class booking, and attendance tracking',
            'Digital coaching, workout delivery, and progress monitoring',
            'Wearable device integration and biometric data ingestion',
            'Community features, challenges, and retention gamification'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/fitness-wellness-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Fitness SaaS',
        useCaseLink: ''
    },
    {
        title: 'Legal & Contract SaaS',
        description:
            'Document-centric platforms for law firms, in-house legal teams, and contract-heavy businesses — AI-powered contract analysis, clause extraction, matter management, and e-signature workflows under GDPR and ABA guidelines.',
        points: [
            'AI contract analysis, clause extraction, and risk flagging',
            'Matter management, deadline tracking, and billing automation',
            'Secure document collaboration and version control',
            'E-signature integration and executed document archiving'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/legal-contract-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your LegalTech SaaS',
        useCaseLink: ''
    },
    {
        title: 'Accounting & Finance SaaS',
        description:
            'Cloud accounting, financial reporting, and expense management platforms for SMBs and enterprises — multi-entity consolidation, automated reconciliation, tax compliance, and real-time financial intelligence.',
        points: [
            'Multi-entity accounting, consolidation, and intercompany workflows',
            'Automated bank reconciliation and expense categorization',
            'Tax compliance, audit trail, and regulatory reporting',
            'Real-time financial dashboards and cash flow forecasting'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/accounting-finance-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Accounting SaaS',
        useCaseLink: ''
    },
    {
        title: 'Supply Chain & Procurement SaaS',
        description:
            'End-to-end supply chain visibility platforms — supplier onboarding, purchase order management, inventory optimization, demand forecasting, and logistics coordination for manufacturers and distributors.',
        points: [
            'Supplier onboarding, qualification, and performance management',
            'Purchase order automation and approval workflow management',
            'Demand forecasting, inventory optimization, and reorder logic',
            'Multi-location warehouse coordination and shipment visibility'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/supply-chain-procurement-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Supply Chain SaaS',
        useCaseLink: ''
    },
    {
        title: 'Cybersecurity & Compliance SaaS',
        description:
            'Security management platforms for enterprises managing threat detection, identity governance, vulnerability management, and continuous compliance monitoring under ISO 27001, and NIST CSF.',
        points: [
            'SIEM integration, threat detection, and incident response workflows',
            'Identity governance, access certification, and zero trust enforcement',
            'Vulnerability scanning automation and patch prioritization',
            'Continuous compliance monitoring and audit evidence collection'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/cybersecurity-compliance-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Cybersecurity SaaS',
        useCaseLink: ''
    },
    {
        title: 'IoT & Smart Device SaaS',
        description:
            'Cloud platforms connecting physical devices to operational intelligence — device provisioning, telemetry ingestion, real-time monitoring, predictive maintenance, and edge computing for manufacturers and industrial operators.',
        points: [
            'Device provisioning, firmware management, and OTA updates',
            'Real-time telemetry ingestion and time-series data processing',
            'Predictive maintenance alerting and asset performance analytics',
            'Edge computing integration and offline-first device sync'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/iot-smart-device-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your IoT SaaS',
        useCaseLink: ''
    },
    {
        title: 'Media & Content SaaS',
        description:
            'Video streaming, podcast hosting, digital asset management, and content monetization platforms — for media companies, creators, and enterprises managing large content libraries at scale.',
        points: [
            'Video transcoding, multi-CDN delivery, and adaptive bitrate streaming',
            'Content management, tagging, metadata, and search indexing',
            'Subscription paywalls, pay-per-view, and ad monetization models',
            'Audience analytics, engagement tracking, and retention reporting'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/media-content-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Media SaaS',
        useCaseLink: ''
    },
    {
        title: 'Travel & Hospitality Booking SaaS',
        description:
            'Booking engines, itinerary management, and distribution platforms for travel operators, OTAs, and hospitality groups — with GDS integration, dynamic pricing, and multi-channel inventory management.',
        points: [
            'Booking engine with real-time availability and dynamic pricing',
            'GDS and OTA integration for multi-channel inventory distribution',
            'Itinerary management, upsell automation, and loyalty integration',
            'Revenue management analytics and occupancy optimization'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/travel-hospitality-booking-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Travel SaaS',
        useCaseLink: ''
    },
    {
        title: 'Analytics & Business Intelligence SaaS',
        description:
            'Self-serve intelligence platforms that turn raw operational data into decisions — custom data warehouses, embedded BI dashboards, ETL pipelines, and AI-powered forecasting built as a standalone product or embedded inside an existing SaaS platform as a retention and expansion driver.',
        points: [
            'Self-serve BI dashboards with role-scoped data access per tenant',
            'Custom ETL pipelines and Snowflake/BigQuery data warehouse integration',
            'AI-driven forecasting, anomaly detection, and usage telemetry',
            'Embedded analytics delivered inside your SaaS product UI'
        ],
        footer: '',
        image: cdnUrl('Saas-developement-company/analytics-business-intelligence-saas.webp'),
        viewCaseStudyLink: '/portfolio',
        viewCaseStudyLabel: 'Build Your Analytics SaaS',
        useCaseLink: ''
    }
];

export const saasCapabilitiesData = [
    {
        header: 'Core Features',
        children: [
            {
                heading: 'User Authentication & Secure Login',
                body: 'Email/password, social login, magic link, and biometric support with brute-force protection, session expiry, device trust management, and failed attempt logging — the entry point every security auditor examines first.'
            },
            {
                heading: 'Multi-Factor Authentication (MFA)',
                body: 'TOTP authenticator app, SMS OTP, email OTP, and FIDO2/WebAuthn hardware key support — with per-tenant enforcement policies that enterprise buyers can mandate across their entire user base.'
            },
            {
                heading: 'Single Sign-On (SSO)',
                body: "SAML 2.0 and OAuth2/OIDC federation with Okta, Azure AD, Google Workspace, and Active Directory — the feature that unblocks every enterprise procurement decision and removes password management from the buyer's IT team."
            },
            {
                heading: 'Role-Based Access Control (RBAC)',
                body: 'Hierarchical role definitions, tenant-scoped permissions, resource-level access controls, and custom role creation — granular enough to satisfy enterprise security reviews, configurable enough for admins to manage without engineering support.'
            },
            {
                heading: 'User Management & Provisioning',
                body: 'Invite flows, bulk import, SCIM-based automated provisioning and deprovisioning, seat limit enforcement, and full user lifecycle management from onboarding through account closure.'
            },
            {
                heading: 'Multi-Tenant Data Architecture',
                body: 'Shared-schema or isolated-schema tenancy with complete data isolation, per-tenant configuration, and resource governance — the architectural decision that separates a production SaaS platform from a web application with multiple accounts.'
            },
            {
                heading: 'Admin Panel & Operations Dashboard',
                body: 'Tenant management, user provisioning, subscription administration, feature flag control, platform configuration, and usage analytics — everything needed to operate a SaaS business without direct database access.'
            },
            {
                heading: 'Subscription & Plan Management',
                body: 'Plan creation, upgrade and downgrade flows, trial management, seat-based and usage-based entitlement enforcement, and plan limit controls — the commercial layer connecting product usage to revenue.'
            },
            {
                heading: 'Billing & Payment Processing',
                body: 'Stripe, Braintree, and regional processor integration with subscription billing, one-time charges, proration, tax calculation, and PCI-DSS-compliant payment flows for global SaaS distribution.'
            },
            {
                heading: 'Invoice & Revenue Management',
                body: 'Automated invoice generation, PDF delivery, payment history, credit notes, refund processing, and revenue recognition logic aligned with ASC 606 and IFRS 15 requirements.'
            },
            {
                heading: 'Dunning & Failed Payment Recovery',
                body: 'Automated retry sequences, customer-facing payment update flows, grace period management, and churn-prevention logic that recovers failed payments before they become cancellations.'
            },
            {
                heading: 'Self-Service Customer Portal',
                body: 'Subscription management, billing history, seat management, payment method updates, usage dashboards, and plan upgrade flows — reducing support ticket volume and enabling customer-led expansion revenue.'
            },
            {
                heading: 'Onboarding & Activation Flows',
                body: 'Guided product tours, progress checklists, behavior-triggered email sequences, empty state design, and first-value-moment engineering — closing the activation gap where most SaaS products lose 60–80% of trial users.'
            },
            {
                heading: 'In-App Notification Center',
                body: 'Real-time notification center with read/unread state, notification history, deep-link routing to context, and tenant-level preference management — keeping users informed without overwhelming them.'
            },
            {
                heading: 'Transactional Email System',
                body: 'Transactional email via SendGrid or AWS SES with template management, delivery tracking, bounce handling, unsubscribe management, and per-tenant sender branding across every system event.'
            },
            {
                heading: 'SMS & Push Notifications',
                body: 'SMS via Twilio for time-sensitive alerts, push notifications for mobile SaaS clients, and webhook delivery for system integrations — unified multi-channel communication with a single preference management layer.'
            },
            {
                heading: 'Search & Filtering',
                body: 'Full-text search across tenant-scoped data, faceted filtering, saved searches, autocomplete, sort controls, and pagination — search infrastructure that scales with data volume without degrading result quality or response time.'
            },
            {
                heading: 'Reporting & Analytics Dashboard',
                body: 'Pre-built operational dashboards, custom report builder, scheduled report delivery, data export to CSV and PDF, and role-scoped data visibility — self-serve reporting that answers the business questions that justify renewal decisions.'
            },
            {
                heading: 'File Upload & Storage',
                body: 'Drag-and-drop upload with progress tracking, chunked transfer for large files, file type and size validation, and virus scanning — file handling infrastructure that works reliably across every browser and network condition.'
            },
            {
                heading: 'Document Management',
                body: 'Document versioning, folder hierarchy, tagging, metadata management, CDN-accelerated delivery, and signed URL access control — storage infrastructure built for compliance-sensitive and media-heavy SaaS platforms.'
            },
            {
                heading: 'In-App Messaging & Collaboration',
                body: 'Threaded comments, inline annotations, @mentions, real-time collaborative editing with presence indicators, and activity feeds — keeping collaboration inside the platform rather than losing it to email threads and Slack channels.'
            },
            {
                heading: 'Audit Trail & Activity Log',
                body: 'Immutable, timestamped, actor-attributed event log of every user action, data change, and system event — exportable to SIEM systems with configurable retention policies per data classification and regulatory requirement.'
            },
            {
                heading: 'API Access & Developer Keys',
                body: 'RESTful and GraphQL APIs with OpenAPI documentation, API key management, scoped permissions, rate limiting, and versioning — programmatic access that enterprise and technical users require before they will sign a contract.'
            },
            {
                heading: 'Webhook Management',
                body: "Event subscription management, delivery logging, retry logic on failure, and failure alerting — making your SaaS a reliable integration partner for every tool already running in your customers' operations stack."
            },
            {
                heading: 'Data Export & Portability',
                body: 'User-initiated full data export, structured format delivery in JSON, CSV, and XML, and scheduled export automation — a compliance requirement under GDPR and CCPA and a trust signal for every enterprise procurement team.'
            },
            {
                heading: 'Cookie Consent & Privacy Controls',
                body: 'GDPR and CCPA-compliant consent management, cookie categorization, preference storage, and consent audit logging — legal infrastructure that must exist before the first user lands on the platform in any regulated market.'
            },
            {
                heading: 'Terms & Privacy Version Management',
                body: 'Version-controlled terms of service and privacy policy with user acceptance tracking, re-acceptance triggers on document update, and full acceptance audit log — legal compliance infrastructure for every jurisdiction you operate in.'
            },
            {
                heading: 'Localization & Internationalization',
                body: 'Multi-language UI support, locale-specific date and number formatting, timezone management, currency display, and right-to-left layout support — baseline requirements for any SaaS product targeting more than one geography.'
            }
        ]
    },
    {
        header: 'Advanced Features',
        children: [
            {
                heading: 'White-Label & Custom Branding',
                body: 'Per-tenant custom logo, color scheme, custom domain, branded email sender identity, and white-labeled login page — enabling reseller programs, channel partnerships, and multi-brand portfolios on a single codebase.'
            },
            {
                heading: 'Feature Flags & Entitlement Management',
                body: 'Granular feature flag control per tenant, plan, or user segment — enabling phased rollouts, A/B testing, beta access programs, and plan-based feature gating without requiring code deployments or engineering involvement.'
            },
            {
                heading: 'SCIM Provisioning & Directory Sync',
                body: 'Automated user provisioning and deprovisioning via SCIM 2.0, bidirectional directory sync with Okta, Azure AD, and Google Workspace — lifecycle management triggered directly by HR and identity provider system events.'
            },
            {
                heading: 'Advanced RBAC & Attribute-Based Access Control',
                body: 'Policy-based access control combining roles with contextual attributes — time, location, data classification, and resource ownership — for SaaS platforms requiring finer access granularity than role hierarchies alone can provide.'
            },
            {
                heading: 'Workflow Automation Engine',
                body: 'Visual rule builders, multi-step approval chains, conditional branching, SLA tracking, and third-party trigger integrations — configurable automation that non-technical administrators can own and modify without engineering involvement.'
            },
            {
                heading: 'Custom Reporting & BI Engine',
                body: 'Drag-and-drop custom report builder, scheduled delivery, cohort analysis, funnel tracking, retention reporting, and data warehouse integration with Snowflake and BigQuery — self-serve intelligence that reduces analyst dependency.'
            },
            {
                heading: 'Data Pipeline & ETL Infrastructure',
                body: 'Custom ETL pipelines, data transformation logic, batch processing, and real-time streaming — connecting the SaaS data layer to downstream analytics platforms, data warehouses, and business intelligence tools customers already operate.'
            },
            {
                heading: 'Microservices & Event-Driven Architecture',
                body: 'Domain-driven decomposition, API gateway, service mesh, distributed tracing, circuit breakers, and Apache Kafka event streaming for CQRS patterns — architecture that scales individual services independently under real production tenant load.'
            },
            {
                heading: 'Developer Portal & SDK',
                body: 'Interactive API explorer, SDK generation for major languages, integration guides, sandbox environment, and developer-facing documentation — the extensibility layer that turns a SaaS product into a platform other teams build on.'
            },
            {
                heading: 'Marketplace & Integration Hub',
                body: 'App marketplace infrastructure, third-party integration listings, OAuth-based app authorization, and partner revenue sharing — the ecosystem layer that drives organic growth and significantly reduces competitive churn.'
            },
            {
                heading: 'Multi-Region Deployment & Data Residency',
                body: 'Geographic data isolation, latency-optimized routing, regional failover, and configurable data residency per tenant — infrastructure that satisfies data sovereignty requirements and enterprise governance policies across global markets.'
            },
            {
                heading: 'Tenant Isolation & Resource Governance',
                body: 'Per-tenant compute quotas, rate limiting, resource usage monitoring, and noisy neighbor prevention — ensuring consistent performance SLA delivery across every tenant on the platform regardless of concurrent load patterns.'
            },
            {
                heading: 'Customer Health Scoring',
                body: 'Composite health scores combining login frequency, feature adoption depth, support ticket volume, and billing events — giving customer success teams early warning signals that prevent churn before it registers in revenue metrics.'
            },
            {
                heading: 'Embedded Analytics & Product Telemetry',
                body: 'In-product usage analytics, feature adoption tracking, session recording, heatmaps, and funnel analysis — behavioral intelligence that informs roadmap prioritization, sales conversations, and product-led growth strategies.'
            },
            {
                heading: 'SLA Monitoring & Incident Management',
                body: 'Real-time platform health monitoring, per-tenant error rate dashboards, defined severity tiers with committed response times, automated escalation paths, and post-incident reporting backed by operational runbooks.'
            },
            {
                heading: 'Disaster Recovery & Business Continuity',
                body: 'Automated multi-region backups, point-in-time recovery, tested failover procedures, and documented RTO/RPO targets — recovery infrastructure that satisfies enterprise procurement requirements and cyber insurance underwriting criteria.'
            },
            {
                heading: 'Advanced Security Controls',
                body: 'IP whitelisting, session binding, anomalous login detection, security event alerting, dependency vulnerability scanning, and OWASP Top 10 protection — security depth that passes enterprise vendor assessment questionnaires at every maturity level.'
            },
            {
                heading: 'Referral & Affiliate Program Infrastructure',
                body: 'Referral link generation, attribution tracking, reward management, payout automation, and affiliate portal — growth infrastructure that systematically monetizes word-of-mouth without requiring a separate marketing technology platform.'
            },
            {
                heading: 'Programmatic Reporting API',
                body: 'Structured API access to all platform analytics and operational data — enabling enterprise customers to pull SaaS data into their own BI tools, data warehouses, and executive dashboards entirely on their own schedule and terms.'
            },
            {
                heading: 'Multi-Currency & Global Pricing',
                body: 'Currency detection, localized pricing display, multi-currency transaction processing, exchange rate management, and regional tax handling — commercial infrastructure for SaaS products competing in more than one geographic market.'
            }
        ]
    },
    {
        header: 'AI-Integrated Features',
        children: [
            {
                heading: 'LLM Integration & Orchestration',
                body: 'OpenAI, Anthropic Claude, and open-source LLM integration with prompt engineering, context management, RAG pipeline architecture, and response quality controls — AI embedded at the architecture level from sprint one, not added post-launch.'
            },
            {
                heading: 'AI Assistant & In-Product Copilot',
                body: "Contextual AI assistant with access to the user's own platform data, natural language query interface, proactive workflow suggestions triggered by behavioral state, draft generation, and conversation history with context persistence across sessions."
            },
            {
                heading: 'Semantic Search & Vector Retrieval',
                body: 'Embedding-based semantic search using Pinecone, Weaviate, or pgvector — returning meaning-aware results across documents, records, and knowledge bases even when users cannot articulate the precise terminology to find what they need.'
            },
            {
                heading: 'AI Document Processing & Extraction',
                body: 'Automated document ingestion, OCR, NLP-based structured data extraction, intelligent classification, and automated routing — replacing manual data entry workflows at the scale that legal, financial, and healthcare SaaS platforms demand.'
            },
            {
                heading: 'Predictive Analytics & Churn Forecasting',
                body: 'Custom ML models for churn prediction, usage forecasting, upsell propensity scoring, and demand forecasting — trained on proprietary behavioral data with production monitoring and automated retraining as usage patterns shift over time.'
            },
            {
                heading: 'Anomaly Detection & Predictive Alerting',
                body: 'ML-based detection of operational anomalies, behavioral outliers, and performance degradation patterns — with predictive alerts surfaced before thresholds are breached rather than after damage has already registered in business metrics.'
            },
            {
                heading: 'Recommendation Engine',
                body: 'Collaborative and content-based filtering, real-time personalization, and dynamic content and product ranking — recommendation infrastructure that improves measurably with usage volume and drives quantifiable increases in expansion revenue.'
            },
            {
                heading: 'NLP Workflow Automation',
                body: 'Natural language processing embedded in operational workflows — automated document classification, entity extraction, sentiment analysis, and intent routing that eliminates the manual triage layer between data ingestion and operational action.'
            },
            {
                heading: 'Agentic AI Orchestration',
                body: 'Multi-step autonomous AI agents executing complex research, decision, and action sequences without human intervention at each step — built on LangChain, LlamaIndex, or custom orchestration frameworks matched to the specific use case.'
            },

            {
                heading: 'AI-Powered Onboarding Personalization',
                body: 'Behavioral signals from early platform sessions dynamically adjust onboarding paths, surface relevant features, and trigger personalized activation flows — reducing time-to-value and improving trial-to-paid conversions.'
            },
            {
                heading: 'Conversational UI & AI Chatbot',
                body: 'Domain-specific AI assistants with intent detection, multi-turn context memory, human escalation, and CRM integration — replacing first-tier support and guided workflows at enterprise scale.'
            },
            {
                heading: 'ML Pipeline & MLOps Infrastructure',
                body: 'End-to-end ML pipelines covering data labeling, training, evaluation, versioning, deployment, and automated retraining — with monitoring and drift detection to maintain model accuracy over time.'
            },
            {
                heading: 'AI Compliance & Decision Audit',
                body: 'Audit-ready AI decision logging, regulatory monitoring, automated policy checks, and anomaly detection — ensuring explainability and traceability for regulated SaaS environments.'
            },
            {
                heading: 'Generative Content & Draft Automation',
                body: 'LLM-powered generation of emails, reports, proposals, and structured documents — triggered by workflows or user actions, with human review layers for sensitive use cases.'
            },
            {
                heading: 'AI-Powered Search Ranking',
                body: 'Intent-aware search with personalized ranking based on user behavior and hybrid keyword-semantic retrieval — improving relevance as user interaction data grows.'
            },
            {
                heading: 'AI KYC & Fraud Detection',
                body: 'ML-driven transaction monitoring, identity risk scoring, behavioral biometrics, and integrations with fraud detection tools — enabling automated protection for financial workflows.'
            },
            {
                heading: 'AI-Driven Customer Success',
                body: 'Automated health scoring, churn prediction, and next-best-action recommendations — enabling proactive customer engagement and improving retention outcomes.'
            }
        ]
    }
];
const customEndToEndChevronUp = cdnImage('web3/Web3-Services-ChevronUp.svg');
const customEndToEndChevronDown = cdnImage('web3/Web3-Services-ChevronDown.svg');
const serviceArchitectureImg = cdnUrl('Saas-developement-company/software-architecture-strategy-consulting.svg');
const serviceApplicationImg = cdnUrl('Saas-developement-company/custom-software-application-development.svg');
const serviceCloudImg = cdnUrl('Saas-developement-company/cloud-native-development-migration.svg');
const serviceApiImg = cdnUrl('Saas-developement-company/api-development-third-party-integration.svg');
const serviceMvpImg = cdnUrl('Saas-developement-company/mvp-development-for-startups.svg');
const serviceLegacyImg = cdnUrl('Saas-developement-company/legacy-system-modernization.svg');
const serviceSecurityImg = cdnUrl('Saas-developement-company/security-audit-code-review.svg');
const servicePostLaunchImg = cdnUrl('Saas-developement-company/post-launch-support-optimization.svg');
export const saasEndToEndData = {
    chevronUp: customEndToEndChevronUp,
    chevronDown: customEndToEndChevronDown,
    services: [
        {
            icon: serviceArchitectureImg,
            title: 'SaaS Architecture & Strategy Consulting',
            subtitle: '',
            description:
                'Technical architecture review, multi-tenancy model selection, technology stack recommendation, compliance scoping, and go-to-market timeline planning before a line of code is written. The decisions made here determine your SaaS total cost of ownership across the next five years.',
            tags: []
        },
        {
            icon: serviceApplicationImg,
            title: 'Custom SaaS Application Development',
            subtitle: '',
            description:
                'Full-stack custom SaaS development — from multi-tenant database schema through API layer to user interface. Delivered in agile two-week sprints with client review at every milestone. Working software demonstrated every sprint, not only at final handoff.',
            tags: []
        },
        {
            icon: serviceMvpImg,
            title: 'SaaS MVP Development for Startups',
            subtitle: '',
            description:
                'Validated SaaS MVP delivery in 8–12 weeks with production-quality architecture designed to support full-scope extension without a structural rebuild. The architecture is the investment — the MVP validates it. The fastest SaaS app development services for early-stage founders.',
            tags: []
        },
        {
            icon: serviceCloudImg,
            title: 'Cloud-Native SaaS Development & Migration',
            subtitle: '',
            description:
                'Cloud-native SaaS application development on AWS, GCP, and Azure, plus legacy application modernization and SaaS cloud migration — with zero-downtime deployment strategies for platforms serving active users during transition.',
            tags: []
        },
        {
            icon: serviceApiImg,
            title: 'SaaS Integration & API Development',
            subtitle: '',
            description:
                'Custom API development, legacy system integration, and third-party SaaS integration — connecting the software ecosystem your customers already operate into a unified product experience. Salesforce, HubSpot, SAP, NetSuite, Stripe, Twilio, and custom enterprise system integration.',
            tags: []
        },
        {
            icon: serviceApplicationImg,
            title: 'Enterprise SaaS Development',
            subtitle: '',
            description:
                'Production-ready enterprise SaaS platforms for large organizations in regulated industries — covering ERP and CRM integration, enterprise SSO federation, SOC 2 and HIPAA compliance architecture, and SLA-backed operational infrastructure from day one.',
            tags: []
        },
        {
            icon: serviceLegacyImg,
            title: 'SaaS Platform Re-Architecture & Modernization',
            subtitle: '',
            description:
                'Assessment, refactoring, and re-platforming of SaaS applications that have outgrown their original architecture — monolith-to-microservices migration, single-tenant-to-multi-tenant re-architecture, or compliance upgrades that cannot be applied to the existing codebase.',
            tags: []
        },
        {
            icon: serviceSecurityImg,
            title: 'SaaS Security Audit & Compliance',
            subtitle: '',
            description:
                'Static analysis, dynamic testing, dependency scanning, penetration testing, and architecture security review — preparing SaaS platforms for SOC 2 Type II, HIPAA compliance audits, and enterprise customer security assessments. Prioritized remediation plan delivered.',
            tags: []
        },
        {
            icon: servicePostLaunchImg,
            title: 'Post-Launch SaaS Support & Optimization',
            subtitle: '',
            description:
                'Continuous monitoring, performance tuning, security patch management, and feature development — maintained by the engineering team that built your SaaS platform. SLA-backed response times. Not a separate support function working from documentation they did not write.',
            tags: []
        }
    ]
};

// Tech stack icons — reused from existing pages across the codebase.
// Icons that exist only in the shared `Tech-stack-icons/` set (no other
// source available) are kept on that path.
const reactjs = cdnImage('Hire-team/React-js.webp');
const nextJs = cdnImage('Hire-team/next-js.webp');
const vuejs = cdnImage('Hire-team/Vue-js.webp');
const typescript = cdnImage('CryptoDerivativesExchange/TypeScript.webp');
const tailwindCss = cdnImage('Saas-Development/Tailwind-CSS.webp');
const svelte = cdnImage('Hire-team/Svelte.webp');
const reactNative = reactjs;
const flutter = cdnImage('Hire-team/flutter.webp');
const pwa = cdnImage('Hire-Flutter-developer/PWA-Development.webp');

const nodejs = cdnImage('Cross-Platform-App-Development/Node-js.webp');
const go = cdnImage('hybrid-crypto-exchange/Golang.webp');
const python = cdnImage('hire-remote-developer/Python.webp');
const java = cdnImage('hire-remote-developer/Java.webp');
const nestjs = cdnImage('nodejs/NestJS.webp');
const nest = nestjs;
const fastapi = cdnImage('Hire-Prompt-engineer/FastApi.webp');
const django = cdnImage('hire-remote-developer/Django.webp');
const rubyOnRails = cdnImage('Hire-team/Ruby-on-Rails.webp');
const graphql = cdnImage('ai-copliot/GraphQL.webp');
const grpc = cdnImage('ai-copliot/gRPC.webp');

const postgresql = cdnImage('Hire-team/PostgreSQL.webp');
const mysql = cdnImage('Hire-team/mysql.webp');
const mongodb = cdnImage('Hire-team/MongoDB.webp');
const redis = cdnImage('Hire-team/redis.webp');
const elasticsearch = cdnImage('Hire-Prompt-engineer/elasticsearch.webp');
const cassandra = cdnImage('hybrid-crypto-exchange/Cassandra.webp');
const timescaledb = cdnImage('white-label-exchange-development-new/TimescaleDB.webp');
const snowflake = cdnImage('Hire-Machine-Learning-Engineers/Snowflake.webp');
const bigquery = cdnImage('hire-ai-developers/Google-BigQuery.webp');
const pinecone = cdnImage('generative-ai-consulting-company/Pinecone.webp');

const openai = cdnImage('hire-remote-developer/OpenAI-GPT.webp');
const anthropic = cdnImage('ai-copliot-development-new/anthropic.webp');
const langchain = cdnImage('generative-ai-consulting-company/LangChain.webp');
const huggingface = cdnImage('generative-ai-consulting-company/Transformers-Hugging-Face.webp');
const tensorflow = cdnImage('generative-ai-integration/TensorFlow.webp');
const tensorFlow = cdnImage('hire-remote-developer/TensorFlow.webp');
const pytorch = cdnImage('generative-ai-integration/PyTorch.webp');
const scikitLearn = cdnImage('hire-remote-developer/Scikit-learn.webp');
const llamaindex = cdnImage('generative-ai-consulting-company/LlamaIndex.webp');
const weaviate = cdnImage('generative-ai-consulting-company/Weaviate.webp');
const pgvector = cdnImage('Tech-stack-icons/pgvector.webp'); // only in Tech-stack-icons

const aws = cdnImage('generative-ai-integration/GEN-ai-interation/aws.webp');
const awsTech = cdnImage('generative-ai-integration/AWS-AI-Services.webp');
const googleCloud = cdnImage('crypto-wallet-icons/GCP.webp');
const azure = cdnImage('hire-remote-developer/Microsoft-Azure.webp');
const docker = cdnImage('generative-ai-integration/Docker.webp');
const kubernetes = cdnImage('generative-ai-integration/Kubernetes.webp');
const kubernet = cdnImage('hire-remote-developer/Kubernetes.webp');
const terraform = cdnImage('Hire-Blockchain-developer/Terraform.webp');
const githubActions = cdnImage('Chatgpt-integration-services/GitHub-Actions.webp');
const argocd = cdnImage('Software-Development-Consulting-Services/Argo-CD.webp');
const prometheus = cdnImage('hire-remote-developer/Prometheus.webp');
const grafana = cdnImage('AI-Deployment-Company/Grafana.webp');

const stripe = cdnImage('Crypto-Payment-Gateway/Stripe.webp');
const chargebee = cdnUrl('Saas-developement-company/chargebee.svg');
const recurly = cdnUrl('Saas-developement-company/Recurly.svg');
const braintree = cdnImage('ios/Braintree.webp');
const paypal = cdnImage('Crypto-Payment-Gateway/Paypal.webp');
const paddle = cdnUrl('Saas-developement-company/paddle.svg');
const zuora = cdnUrl('Saas-developement-company/Zuora.svg');

const vault = cdnImage('decentralised/HashiCorp-Vault.webp');
const cloudflare = cdnImage('Custom-cms-development-services/Cloudflare.webp');
const kms = cdnImage('decentralised/AWS-KMS.webp');
const snyk = cdnImage('custom-software-development-company/snyk.webp');
const owaspZap = cdnImage('Hire-web-developers/OWASP-Practices.webp');
const sonarqube = cdnImage('bitcoin-development-wallet/SonarQube.webp');
const tls = cdnImage('crypto-market-making/TLS.webp');
const aes = cdnImage('Multicurrency-wallet-development/AES-Encryption.webp');

const kafka = cdnImage('generative-ai-integration/Apache-Kafka.webp');
const rabbitmq = cdnImage('python-images/RabbitMQ.webp');
const sqs = cdnImage('Hire-Node-js-Developers/AWS-SQS.webp');
const redisPubSub = cdnImage('Hire-Node-js-Developers/Google-Pub-Sub.webp');
const websocket = cdnImage('crypto-exchange-revamp-file/websocket.webp');
const grpcStreaming = grpc;
const eventbridge = cdnImage('Tech-stack-icons/eventbridge.webp'); // only in Tech-stack-icons

export const saasAiStackData = [
    {
        category: 'Frontend & Web',
        items: [
            {label: 'React.js', icon: reactjs},
            {label: 'Next.js', icon: nextJs},
            {label: 'Vue.js', icon: vuejs},
            {label: 'TypeScript', icon: typescript},
            {label: 'Tailwind CSS', icon: tailwindCss},
            {label: 'Svelte', icon: svelte},
            {label: 'React Native', icon: reactNative},
            {label: 'Flutter', icon: flutter},
            {label: 'PWA', icon: pwa}
        ]
    },
    {
        category: 'Backend & APIs',
        items: [
            {label: 'Node.js', icon: nodejs},
            {label: 'Go (Golang)', icon: go},
            {label: 'Python', icon: python},
            {label: 'Java (Spring Boot)', icon: java},
            {label: 'NestJS', icon: nestjs},
            {label: 'FastAPI', icon: fastapi},
            {label: 'Django', icon: django},
            {label: 'Ruby on Rails', icon: rubyOnRails},
            {label: 'GraphQL', icon: graphql},
            {label: 'gRPC', icon: grpc}
        ]
    },
    {
        category: 'Databases & Storage',
        items: [
            {label: 'PostgreSQL', icon: postgresql},
            {label: 'MySQL', icon: mysql},
            {label: 'MongoDB', icon: mongodb},
            {label: 'Redis', icon: redis},
            {label: 'Elasticsearch', icon: elasticsearch},
            {label: 'Cassandra', icon: cassandra},
            {label: 'TimescaleDB', icon: timescaledb},
            {label: 'Snowflake', icon: snowflake},
            {label: 'BigQuery', icon: bigquery},
            {label: 'Pinecone', icon: pinecone}
        ]
    },
    {
        category: 'AI & Machine Learning',
        items: [
            {label: 'OpenAI API', icon: openai},
            {label: 'Anthropic Claude', icon: anthropic},
            {label: 'LangChain', icon: langchain},
            {label: 'HuggingFace', icon: huggingface},
            {label: 'Tensor Flow', icon: tensorflow},
            {label: 'Py Torch', icon: pytorch},
            {label: 'scikit-learn', icon: scikitLearn},
            {label: 'LlamaIndex', icon: llamaindex},
            {label: 'Weaviate', icon: weaviate},
            {label: 'pgvector', icon: pgvector}
        ]
    },
    {
        category: 'Cloud & DevOps',
        items: [
            {label: 'AWS', icon: aws},
            {label: 'Google Cloud', icon: googleCloud},
            {label: 'Microsoft Azure', icon: azure},
            {label: 'Docker', icon: docker},
            {label: 'Kubernetes', icon: kubernetes},
            {label: 'Terraform', icon: terraform},
            {label: 'GitHub Actions', icon: githubActions},
            {label: 'ArgoCD', icon: argocd},
            {label: 'Prometheus', icon: prometheus},
            {label: 'Grafana', icon: grafana}
        ]
    },
    {
        category: 'Billing & Payments',
        items: [
            {label: 'Stripe', icon: stripe},
            {label: 'Chargebee', icon: chargebee},
            {label: 'Recurly', icon: recurly},
            {label: 'Braintree', icon: braintree},
            {label: 'PayPal', icon: paypal},
            {label: 'Paddle', icon: paddle},
            {label: 'Zuora', icon: zuora}
        ]
    },
    {
        category: 'Security & Compliance',
        items: [
            {label: 'HashiCorp Vault', icon: vault},
            {label: 'Cloudflare WAF', icon: cloudflare},
            {label: 'AWS KMS', icon: kms},
            {label: 'Snyk', icon: snyk},
            {label: 'OWASP ZAP', icon: owaspZap},
            {label: 'SonarQube', icon: sonarqube},
            {label: 'TLS 1.3', icon: tls},
            {label: 'AES-256', icon: aes}
        ]
    },
    {
        category: 'Messaging & Streaming',
        items: [
            {label: 'Apache Kafka', icon: kafka},
            {label: 'RabbitMQ', icon: rabbitmq},
            {label: 'AWS SQS', icon: sqs},
            {label: 'Redis Pub/Sub', icon: redisPubSub},
            {label: 'WebSocket', icon: websocket},
            {label: 'gRPC Streaming', icon: grpcStreaming},
            {label: 'AWS EventBridge', icon: eventbridge}
        ]
    }
];

export const saasTechnology = [
    {
        heading: 'Frontend & Web',
        description: '',
        values: [
            {icon: reactjs, label: 'React.js'},
            {icon: nextJs, label: 'Next.js'},
            {icon: vuejs, label: 'Vue.js'},
            {icon: typescript, label: 'TypeScript'},
            {icon: tailwindCss, label: 'Tailwind CSS'},
            {icon: svelte, label: 'Svelte'},
            {icon: reactNative, label: 'React Native'},
            {icon: flutter, label: 'Flutter'},
            {icon: pwa, label: 'PWA'}
        ]
    },
    {
        heading: 'Backend & APIs',
        description: '',
        values: [
            {icon: nodejs, label: 'Node.js'},
            {icon: go, label: 'Go (Golang)'},
            {icon: python, label: 'Python'},
            {icon: java, label: 'Java (Spring Boot)'},
            {icon: nest, label: 'NestJS'},
            {icon: fastapi, label: 'FastAPI'},
            {icon: django, label: 'Django'},
            {icon: rubyOnRails, label: 'Ruby on Rails'},
            {icon: graphql, label: 'GraphQL'},
            {icon: grpc, label: 'gRPC'}
        ]
    },
    {
        heading: 'Databases & Storage',
        description: '',
        values: [
            {icon: postgresql, label: 'PostgreSQL'},
            {icon: mysql, label: 'MySQL'},
            {icon: mongodb, label: 'MongoDB'},
            {icon: redis, label: 'Redis'},
            {icon: elasticsearch, label: 'Elasticsearch'},
            {icon: cassandra, label: 'Cassandra'},
            {icon: timescaledb, label: 'TimescaleDB'},
            {icon: snowflake, label: 'Snowflake'},
            {icon: bigquery, label: 'BigQuery'},
            {icon: pinecone, label: 'Pinecone'}
        ]
    },
    {
        heading: 'AI & Machine Learning',
        description: '',
        values: [
            {icon: openai, label: 'OpenAI API'},
            {icon: anthropic, label: 'Anthropic Claude'},
            {icon: langchain, label: 'LangChain'},
            {icon: huggingface, label: 'HuggingFace'},
            {icon: tensorFlow, label: 'Tensor Flow'},
            {icon: pytorch, label: 'Py Torch'},
            {icon: scikitLearn, label: 'scikit-learn'},
            {icon: llamaindex, label: 'LlamaIndex'},
            {icon: weaviate, label: 'Weaviate'},
            {icon: pgvector, label: 'pgvector'}
        ]
    },
    {
        heading: 'Cloud & DevOps',
        description: '',
        values: [
            {icon: awsTech, label: 'AWS'},
            {icon: googleCloud, label: 'Google Cloud'},
            {icon: azure, label: 'Microsoft Azure'},
            {icon: docker, label: 'Docker'},
            {icon: kubernet, label: 'Kubernetes'},
            {icon: terraform, label: 'Terraform'},
            {icon: githubActions, label: 'GitHub Actions'},
            {icon: argocd, label: 'ArgoCD'},
            {icon: prometheus, label: 'Prometheus'},
            {icon: grafana, label: 'Grafana'}
        ]
    },
    {
        heading: 'Billing & Payments',
        description: '',
        values: [
            {icon: stripe, label: 'Stripe'},
            {icon: chargebee, label: 'Chargebee'},
            {icon: recurly, label: 'Recurly'},
            {icon: braintree, label: 'Braintree'},
            {icon: paypal, label: 'PayPal'},
            {icon: paddle, label: 'Paddle'},
            {icon: zuora, label: 'Zuora'}
        ]
    },
    {
        heading: 'Security & Compliance',
        description: '',
        values: [
            {icon: vault, label: 'HashiCorp Vault'},
            {icon: cloudflare, label: 'Cloudflare WAF'},
            {icon: kms, label: 'AWS KMS'},
            {icon: snyk, label: 'Snyk'},
            {icon: owaspZap, label: 'OWASP ZAP'},
            {icon: sonarqube, label: 'SonarQube'},
            {icon: tls, label: 'TLS 1.3'},
            {icon: aes, label: 'AES-256'}
        ]
    },
    {
        heading: 'Messaging & Streaming',
        description: '',
        values: [
            {icon: kafka, label: 'Apache Kafka'},
            {icon: rabbitmq, label: 'RabbitMQ'},
            {icon: sqs, label: 'AWS SQS'},
            {icon: redisPubSub, label: 'Redis Pub/Sub'},
            {icon: websocket, label: 'WebSocket'},
            {icon: grpcStreaming, label: 'gRPC Streaming'},
            {icon: eventbridge, label: 'AWS EventBridge'}
        ]
    }
];
export const saasPricingData = {
    tiers: [
        {
            tier: 'Fixed-Price SaaS Project',
            price: 'From $40,000',
            bestFor: 'Ideal for: Defined-scope builds with launch deadlines',
            features: [
                'Fully scoped SaaS deliverables with milestone-based payment',
                'Fixed timeline, fixed budget — contractually guaranteed',
                'Discovery sprint included at no charge',
                'Board-ready delivery documentation on completion',
                'Architecture Decision Records throughout'
            ],
            buttonText: 'Get a Fixed-Price Proposal'
        },
        {
            tier: 'Dedicated Development Team',
            price: 'From $80,000',
            bestFor: 'Ideal for: Ongoing product development with evolving requirements',
            features: [
                '3–15 senior SaaS developers embedded as your product team',
                'Weekly velocity reporting with DORA metrics',
                'NDA + full IP transfer from Day 1',
                'Team scales up/down with 2-week notice',
                'CTO advisory included for strategic architecture decisions'
            ],
            buttonText: 'Build Your SaaS Team',
            featured: true,
            href: meetingLink,
            external: true
        },
        {
            tier: 'Staff Augmentation',
            price: 'Custom Pricing',
            bestFor: 'Ideal for: Plugging specific SaaS engineering skill gaps',
            features: [
                'Senior SaaS engineers placed within 2-3 business days',
                'Works inside your existing toolchain and sprint cadence',
                '100% time on your project — no shared resources',
                'Minimum 3-month engagement, no lock-in after',
                'Hire SaaS developers or full-stack engineers on demand'
            ],
            buttonText: 'Hire SaaS Developers',
            href: '/hire-saas-developers'
        },
        {
            tier: 'SaaS Development Outsourcing',
            price: 'Custom Pricing',
            bestFor: 'Ideal for: Full outsource from discovery to operations',
            features: [
                'End-to-end SaaS development outsourcing with full ownership',
                'Dedicated PM, Tech Lead, QA Lead, and DevOps engineer',
                'Weekly sprint demos and async communication protocols',
                'Architecture documentation and knowledge transfer throughout',
                'Nearshore SaaS development for US/Canada-timezone overlap'
            ],
            buttonText: 'Explore SaaS Outsourcing',
            href: meetingLink,
            external: true
        }
    ]
};

const daljitsingh = cdnImage('about-us-new/Daljit-Singh.webp');
const copilotExpertHarry = cdnImage('about-us-new/Harry-Dhillon.webp');
export const saasExpertsData = [
    {
        name: 'Daljit Singh',
        designation: 'Co-founder & Director of Blockchain & AI Technology, Debut Infotech',
        image: daljitsingh,
        linkedin: 'https://www.linkedin.com/in/daljit-singh-panesar-522b3918',
        quote: "We've stepped into enough mid-crisis blockchain engagements to know exactly where things break down. Scope that was never properly defined. Architecture decisions rushed under deadline pressure. Integration dependencies nobody accounted for until they became blockers. The process we run today exists because of those lessons — and it's why our clients reach production without the chaos that derails most enterprise blockchain programs."
    },
    {
        name: 'Harry Dhillon',
        designation: 'Director – Digital Transformation & Customer Success, Debut Infotech',
        image: copilotExpertHarry,
        linkedin: 'https://www.linkedin.com/in/dhillonharry/',
        quote: "We've worked with enough enterprises navigating AI adoption to see a consistent pattern. Strong ambition, but fragmented execution. Pilots that never scale. Automation efforts that improve tasks but fail to transform outcomes. The approach we drive today is built on bridging that gap — taking AI from isolated experiments to enterprise-wide systems that deliver measurable impact, operational clarity, and sustained growth."
    }
];

const processChevronDown = cdnImage('web3/Web3-Process-ChevronDown.svg');
const processChevronRight = cdnImage('web3/Web3-Process-ChevronRight.svg');
const processCheckIcon = cdnImage('web3/Web3-Process-CheckIcon.svg');
export const saasProcessData = {
    chevronDown: processChevronDown,
    chevronRight: processChevronRight,
    checkIcon: processCheckIcon,
    steps: [
        {
            title: 'SaaS Requirements Discovery',
            phase: 'PHASE 01',
            description:
                'Stakeholders are interviewed separately across functions — executives, daily users, IT, compliance, and customer success — because conflicting SaaS product requirements cost nothing to resolve in discovery and weeks to resolve in development. Every workflow, integration point, data source, and compliance obligation is documented before architecture begins.\n\nThe multi-tenancy model appropriate for your customer structure is decided here: shared-schema, isolated-schema, or hybrid — with the business rationale, performance implications, and compliance considerations documented in an Architecture Decision Record before any code is written.',
            checklistItems: [
                'Requirements Specification',
                'Integration Inventory',
                'Multi-Tenancy Decision',
                'Compliance Scope',
                'Risk Register'
            ],
            tags: ['01 Discovery 2–6 Weeks']
        },
        {
            title: 'Project Planning & Team Assembly',
            phase: 'PHASE 02',
            description:
                'Project scope is defined with explicit out-of-scope boundaries — change requests have a formal process rather than silently expanding the SaaS development engagement. The delivery team is assembled: Lead SaaS Architect, backend and frontend engineers with prior vertical experience, QA lead, DevOps engineer, and UX designer selected based on your compliance and stack requirements.',
            checklistItems: ['Project Plan', 'Scoped Estimate', 'Team Composition', 'KPI Framework', 'Risk Management Plan'],
            tags: ['02 Planning 1–2 Weeks']
        },
        {
            title: 'SaaS Architecture & Product Design',
            phase: 'PHASE 03',
            description:
                'The multi-tenant architecture is defined: database schema, tenant isolation strategy, shared resource governance, per-tenant configuration architecture, subscription billing integration, and the security model. Every significant decision is captured in an Architecture Decision Record — what was decided, what alternatives were considered, and why — creating an auditable rationale that survives team changes.\n\nIn parallel, the UX team observes how end users actually work before designing anything. Wireframes are reviewed for workflow logic before visual design begins. High-fidelity interactive prototypes are tested with real users. Prototype sign-off is a formal gate before development investment is committed.',
            checklistItems: [
                'Architecture Decision Records',
                'Multi-Tenant Schema',
                'API Contracts',
                'Security Model',
                'Billing Architecture',
                'Validated Prototype'
            ],
            tags: ['03 Architecture & Design 2 Weeks – 2 Months']
        },
        {
            title: 'SaaS MVP Development',
            phase: 'PHASE 04',
            description:
                'The minimum viable feature set is defined based on user value per feature and technical dependency order — not by compromising the architectural foundation that will support the full-scope SaaS platform. The MVP is engineered to production-quality standards with the multi-tenant architecture designed to support full-scope extension without structural rebuild. Deployed to a controlled tenant group before general availability — issues surface at low cost, not under full production load.',
            checklistItems: ['Production-Ready MVP', 'Controlled Tenant Deployment', 'User Feedback Analysis', 'Updated Roadmap'],
            tags: ['04 MVP Development 8–16 Weeks']
        },
        {
            title: 'Full SaaS Development & Quality Assurance',
            phase: 'PHASE 05',
            description:
                'Two-week sprints — each beginning with agreed acceptance criteria per item, each ending with a tested increment demonstrated to your stakeholders. Peer review required on every pull request. Feature flags for in-progress work. SaaS-specific testing: multi-tenant isolation tests, subscription lifecycle tests, billing edge cases, SSO federation tests, and concurrent tenant load testing at 3× projected peak before production deployment.\n\nCI/CD pipeline active from Sprint 1 — every merge triggers an automated build, test run, and staging deployment. Security scanning on every build: static analysis, dependency vulnerabilities, OWASP Top 10. Compliance evidence produced for every applicable control before deployment phase opens.',
            checklistItems: [
                'Sprint Demos Every 2 Weeks',
                'Automated Test Suites',
                'Security Scan Reports',
                'Compliance Evidence',
                'Load Test Results'
            ],
            tags: ['05Development & QA 2 Weeks – 6 Months']
        },
        {
            title: 'Production Deployment & Go-Live',
            phase: 'PHASE 06',
            description:
                'All infrastructure defined in version-controlled code before deployment begins. Staged rollout: controlled initial tenant group, full cutover only after monitoring confirms stability. Data migrations executed with validated rollback paths throughout. Multi-tenant monitoring dashboards established: per-tenant error rates, API performance, subscription events, billing processing.',
            checklistItems: [
                'Infrastructure as Code',
                'Staged Rollout',
                'Operational Runbook',
                'Monitoring Dashboards',
                'Knowledge Transfer'
            ],
            tags: ['06 Deployment 1–2 Weeks']
        },
        {
            title: 'Post-Launch Warranty & Hypercare',
            phase: 'PHASE 07',
            description:
                'SaaS platform health is monitored from day one post-launch: per-tenant error rates, API response times, database performance, subscription event processing, and billing error rates. Incidents are managed through defined severity tiers — Critical: 1 hour, High: 4 hours, Medium: 24 hours — with committed response times, not estimates given after an incident opens. All issues are addressed by the engineering team that built the system.',
            checklistItems: ['SLA-Backed Monitoring', 'Incident Management', 'Performance Reports', 'Security Patches'],
            tags: ['07 Warranty Support 3–6 Months']
        },
        {
            title: 'Long-Term SaaS Engineering Partnership',
            phase: 'PHASE 08',
            description:
                'Continued feature development by the same team with full SaaS architectural context — no knowledge transfer overhead, no ramp-up cost when new work is scoped. Planned performance optimization cycles: query analysis, caching layer review, multi-tenant resource governance. Annual <a href="/blog/multi-tenant-saas-architecture-guide" target="_blank">SaaS architecture</a> reviews to proactively identify components approaching capacity limits. Regulatory framework monitoring: compliance obligation changes tracked and implemented as standards evolve.',
            checklistItems: ['Feature Development', 'Architecture Reviews', 'Compliance Monitoring', 'Security Cadence'],
            tags: ['08 Long-Term Partnership Ongoing']
        }
    ]
};

export const saasIndustrySolutionsData = {
    cards: [
        {
            icon: cdnUrl('Saas-developement-company/financial-services-fintech-icon.svg'),
            category: 'Financial Services & FinTech SaaS',
            heading: 'Custom SaaS for Regulated Financial Institutions',
            description:
                'Secure, compliance-aligned platforms for banks, fintech companies, asset managers, and payment providers operating under strict regulatory, audit, and data sovereignty requirements across global markets.',
            tags: ['PCI-DSS', 'GDPR', 'MiCA', 'Basel III', 'SOC 2–Aligned Controls'],
            bullets: [
                'Core banking integration, open banking APIs & ISO 20022 messaging infrastructure',
                'AI-powered fraud detection, AML monitoring & real-time risk scoring systems',
                'Digital lending platforms, KYC/KYB automation & e-signature workflows',
                'Algorithmic trading infrastructure, liquidity systems & portfolio analytics'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/Healthcare-HealthTech.svg'),
            category: 'Healthcare & HealthTech SaaS',
            heading: 'HIPAA-Compliant Clinical & Patient Systems',
            description:
                'Secure, interoperable SaaS platforms for providers, health networks, and life sciences organizations managing sensitive patient data, clinical workflows, and regulatory requirements across care settings.',
            tags: ['HIPAA', 'HL7 FHIR', 'FDA 21 CFR Part 11', 'HITRUST', 'GDPR'],
            bullets: [
                'EHR/EMR integration, interoperability layers & FHIR-based API infrastructure',
                'AI-powered clinical decision support & diagnostic assistance systems',
                'Telemedicine, remote patient monitoring & patient engagement platforms',
                'Clinical trial management, eClinical systems & regulatory workflow automation'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/logistics-supply-chain.svg'),
            category: 'Logistics & Supply Chain SaaS',
            heading: 'Real-Time Logistics & Trade Compliance Platforms',
            description:
                'Operational SaaS for 3PLs, freight operators, and global supply chain organizations requiring real-time visibility, multi-carrier coordination, and cross-border trade compliance at scale.',
            tags: ['EDI', 'GDPR', 'CTPAT', 'Global Trade Compliance', 'ISO 28000'],
            bullets: [
                'Fleet tracking, dispatch systems & real-time route optimization infrastructure',
                'Multi-carrier API integration with FedEx, UPS, DHL & regional providers',
                'AI-driven demand forecasting, load balancing & ETA prediction systems',
                'Cross-border logistics platforms, customs automation & trade compliance workflows'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/legal-industry.svg'),
            category: 'Legal & Professional Services SaaS',
            heading: 'AI-Powered Legal & Document Intelligence Platforms',
            description:
                'Secure, document-centric SaaS for law firms and professional services organizations handling sensitive client data, complex matter workflows, and strict confidentiality obligations.',
            tags: ['GDPR', 'ISO 27001', 'SOC 2–Aligned Controls', 'ABA Guidelines'],
            bullets: [
                'AI-powered contract analysis, clause extraction & risk identification systems',
                'Secure document lifecycle management, versioning & encrypted collaboration',
                'Matter management, billing automation, time tracking & profitability analytics',
                'Client portals, workflow automation & deadline management platforms'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/edtech.svg'),
            category: 'Education & EdTech SaaS',
            heading: 'Scalable Learning & Student Intelligence Systems',
            description:
                'Digital SaaS platforms for academic institutions and EdTech companies delivering personalized learning, credentialing, and analytics-driven education at scale.',
            tags: ['FERPA', 'COPPA', 'LTI 1.3', 'SCORM', 'WCAG 2.2 AA'],
            bullets: [
                'LMS and SIS SaaS with full learning ecosystem integrations',
                'AI-driven adaptive learning, content personalization & learner path optimization',
                'Assessment systems, credentialing workflows & learning analytics dashboards',
                'Virtual classroom delivery, proctoring & accessibility-compliant SaaS infrastructure'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/ecommerce-retail.svg'),
            category: 'Retail & eCommerce SaaS',
            heading: 'Composable Commerce & Retail Intelligence Platforms',
            description:
                'Multi-channel SaaS for retailers and DTC brands competing on personalization, operational efficiency, and the composable architecture that replaces monolithic commerce platforms.',
            tags: ['PCI-DSS', 'GDPR', 'CCPA', 'Headless Commerce Standards'],
            bullets: [
                'Omnichannel inventory, order management & fulfillment orchestration systems',
                'AI-powered product recommendations, dynamic pricing & demand forecasting',
                'Headless and composable commerce architecture with CMS integration',
                'Loyalty programs, subscription commerce & post-purchase automation platforms'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/real-estate.svg'),
            category: 'Real Estate & PropTech SaaS',
            heading: 'Property Management & Investment Intelligence Platforms',
            description:
                'Digital SaaS for real estate operators, property managers, and investment firms managing assets, tenants, and transactions across portfolios of any size.',
            tags: ['GDPR', 'CCPA', 'SOC 2–Aligned Controls', 'Fair Housing Act'],
            bullets: [
                'Property management, lease lifecycle & tenant experience SaaS platforms',
                'Investment analytics, valuation modeling & portfolio performance dashboards',
                'Transaction management, MLS integration & digital closing workflow systems',
                'AI-driven rent optimization, occupancy forecasting & market intelligence'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/insurance-insurtech-icon.svg'),
            category: 'Insurance & InsurTech SaaS',
            heading: 'Policy, Underwriting & Claims Intelligence Platforms',
            description:
                'Enterprise SaaS for insurers, MGAs, and InsurTech companies managing policy lifecycle, underwriting decisions, and claims operations under strict regulatory and data governance requirements.',
            tags: ['NAIC', 'ISO 27001', 'SOC 2–Aligned Controls', 'GDPR'],
            bullets: [
                'Policy administration lifecycle automation & configuration management SaaS',
                'AI-powered underwriting, risk modeling & actuarial decision support systems',
                'Claims processing, fraud detection & automated settlement workflow platforms',
                'Broker portals, commission management & performance analytics SaaS'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/manufacturing.svg'),
            category: 'Manufacturing & Industrial SaaS',
            heading: 'MES, IoT & Production Intelligence Systems',
            description:
                'Operational SaaS for manufacturers managing complex production environments, supply chains, quality control, and real-time industrial data under strict safety and regulatory frameworks.',
            tags: ['ISO 9001', 'IEC 62443', 'OSHA', 'ERP Integration Standards'],
            bullets: [
                'ERP integration with SAP, Oracle, NetSuite & Microsoft Dynamics infrastructure',
                'IoT data pipelines, edge computing & predictive maintenance systems',
                'Computer vision platforms for automated quality inspection & defect detection',
                'Digital twin systems, production performance & operational optimization SaaS'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/government-public-sector.svg'),
            category: 'Government & Public Sector SaaS',
            heading: 'Secure Digital Systems for Public Infrastructure',
            description:
                'Enterprise SaaS for government agencies delivering citizen services, managing compliance, and operating under the strictest security mandates and data sovereignty requirements in any sector.',
            tags: ['FedRAMP', 'FISMA', 'NIST 800-53', 'Section 508', 'CJIS'],
            bullets: [
                'ATO-ready SaaS architectures with full security documentation & compliance frameworks',
                'Case management, grants administration & citizen-facing reporting platforms',
                'Digital identity verification, access control & credentialing systems',
                'Citizen portal development & legacy system modernization infrastructure'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/Cybersecurity.svg'),
            category: 'Cybersecurity SaaS',
            heading: 'Security Platforms & Zero Trust Systems',
            description:
                'Secure-by-design SaaS for enterprise security teams managing threats, identity governance, vulnerability programs, and continuous compliance monitoring across complex environments.',
            tags: ['ISO 27001', 'NIST CSF', 'Zero Trust Architecture', 'SOC 2–Aligned Controls'],
            bullets: [
                'SIEM/SOAR integration, threat detection & automated incident response systems',
                'Identity and access management, RBAC & zero trust enforcement platforms',
                'Vulnerability management, scanning automation & patch prioritization systems',
                'Security analytics, compliance monitoring & incident response SaaS infrastructure'
            ]
        },
        {
            icon: cdnUrl('Saas-developement-company/Hr-workforce-management.svg'),
            category: 'HR & Workforce Management SaaS',
            heading: 'People Operations & Workforce Intelligence Platforms',
            description:
                'End-to-end human capital management SaaS replacing fragmented HR toolstacks — covering the full employee lifecycle from recruitment through performance, payroll, and retention analytics.',
            tags: ['GDPR', 'CCPA', 'EEOC', 'ADA', 'SOX–Aligned Controls'],
            bullets: [
                'ATS, onboarding automation & HR workflow orchestration SaaS platforms',
                'Performance management, OKR frameworks & continuous feedback systems',
                'Payroll integration, benefits administration & compliance reporting infrastructure',
                'Workforce analytics, predictive retention modeling & organizational intelligence'
            ]
        }
    ]
};

export const saasComplianceData = {
    pillars: [
        {
            icon: 'layers',
            title: 'Architecture-Stage Compliance',
            description:
                'Audit logging, encryption, and access controls are data model decisions made before sprint one — not retrofitted after a security review.'
        },
        {
            icon: 'evidence',
            title: 'Compliance Evidence Production',
            description:
                'SOC 2, HIPAA, and FedRAMP evidence is produced per control throughout the development cycle — not assembled under audit pressure.'
        },
        {
            icon: 'monitor',
            title: 'Regulatory Framework Monitoring',
            description:
                'Compliance obligation changes tracked and implemented as standards evolve — so your platform stays review-ready continuously, not just at launch.'
        }
    ],
    frameworksHeading: 'Supported Regulatory Frameworks',
    frameworksSubheading: 'Continuous monitoring and evidence production across global standards.',
    frameworks: [
        {abbr: 'SOC 2 Type II', name: 'System and Organization Controls Type II'},
        {abbr: 'ISO/IEC 27001', name: 'Information Security Management Standard'},
        {abbr: 'ISO/IEC 27701', name: 'Privacy Information Management Extension'},
        {abbr: 'GDPR', name: 'General Data Protection Regulation'},
        {abbr: 'HIPAA', name: 'Health Insurance Portability and Accountability Act'},
        {abbr: 'PCI DSS', name: 'Payment Card Industry Data Security Standard'},
        {abbr: 'FedRAMP', name: 'Federal Risk and Authorization Management Program'},
        {abbr: 'FISMA', name: 'Federal Information Security Modernization Act'},
        {abbr: 'NIST CSF', name: 'National Institute of Standards and Technology'},
        {abbr: 'CCPA', name: 'California Consumer Privacy Act'},
        {abbr: 'CPRA', name: 'California Privacy Rights Act'},
        {abbr: 'FERPA', name: 'Family Educational Rights and Privacy Act'},
        {abbr: 'WCAG 2.2 AA', name: 'Web Content Accessibility Guidelines'},
        {abbr: 'ADA', name: 'Americans with Disabilities Act'},
        {abbr: 'GLBA', name: 'Gramm-Leach-Bliley Act'},
        {abbr: 'GxP', name: 'Good Practice Guidelines for Regulated Industries'},
        {abbr: 'FDA 21 CFR', name: 'FDA 21 CFR Part 11 — Life Sciences'},
        {abbr: 'MiCA', name: 'Markets in Crypto-Assets Regulation — EU'},
        {abbr: 'HL7 FHIR', name: 'Health Level 7 Fast Healthcare Interoperability Resources'},
        {abbr: 'PIPEDA', name: 'Personal Information Protection and Electronic Documents Act'},
        {abbr: 'HITECH Act', name: 'Health Information Technology for Economic and Clinical Health Act'},
        {abbr: 'VARA', name: 'Virtual Assets Regulatory Authority — UAE'},
        {abbr: 'CJIS', name: 'Criminal Justice Information Services Security Policy'}
    ]
};

export const saasBreakdownData = {
    tableHeaders: {
        col1: 'SaaS Product Type',
        col2: 'Estimated Cost',
        col3: 'Typical Timeline',
        col4: 'Best For'
    },
    tableRows: [
        {
            projectType: 'SaaS MVP (core feature set)',
            estimatedCost: '$30,000 – $60,000',
            timeline: '8–12 weeks',
            keyDrivers: 'Startups validating product-market fit before full SaaS investment'
        },
        {
            projectType: 'Multi-Tenant SaaS Platform (core features)',
            estimatedCost: '$60,000 – $180,000',
            timeline: '3–6 months',
            keyDrivers: 'Early-stage SaaS companies and B2B product founders'
        },
        {
            projectType: 'Custom Enterprise SaaS Platform',
            estimatedCost: '$150,000 – $500,000',
            timeline: '5–10 months',
            keyDrivers: 'Enterprise digital transformation and regulated industry SaaS'
        },
        {
            projectType: 'AI-Integrated SaaS Product',
            estimatedCost: '$80,000 – $300,000',
            timeline: '3–8 months',
            keyDrivers: 'SaaS businesses replacing manual workflows with embedded AI'
        },
        {
            projectType: 'Regulated SaaS (HIPAA / SOC 2 / PCI-DSS)',
            estimatedCost: '$120,000 – $400,000',
            timeline: '5–9 months',
            keyDrivers: 'HealthTech, FinTech, GovTech, and InsurTech SaaS operators'
        },
        {
            projectType: 'SaaS Mobile Application (iOS + Android)',
            estimatedCost: '$40,000 – $150,000',
            timeline: '10–20 weeks',
            keyDrivers: 'SaaS products requiring native mobile client applications'
        },
        {
            projectType: 'SaaS Re-Architecture & Cloud Migration',
            estimatedCost: '$80,000 – $350,000',
            timeline: '4–9 months',
            keyDrivers: 'Organizations with SaaS that has outgrown its architecture'
        },
        {
            projectType: 'White-Label SaaS Platform',
            estimatedCost: '$100,000 – $400,000',
            timeline: '4–8 months',
            keyDrivers: 'SaaS businesses building reseller or channel partner programs'
        }
    ],
    driversSectionHeading: 'What Drives Saas Development Cost',
    driverCards: [
        {
            icon: cdnUrl('Saas-developement-company/multi-tenancy-architecture-complexity.svg'),
            title: 'Multi-Tenancy Architecture Complexity',
            description:
                'Shared-schema vs. isolated-schema decisions, per-tenant customization scope, and resource governance architecture — the foundational SaaS cost driver. Incorrect decisions here require a full re-architecture later.'
        },
        {
            icon: cdnUrl('Saas-developement-company/compliance-evidence-production.svg'),
            title: 'Compliance Requirements',
            description:
                'HIPAA, SOC 2, and PCI-DSS compliance architecture adds 20–40% to SaaS development cost — covering audit logging, access controls, encryption key management, and compliance evidence production.'
        },
        {
            icon: cdnUrl('Saas-developement-company/ai-ml-integration-depth-icon.svg'),
            title: 'AI & ML Integration Depth',
            description:
                'LLM API integration adds $15–50K. Custom <a href="/blog/role-of-data-in-ml-model-training" target="_blank">ML model training</a> and MLOps infrastructure adds $50–200K+ depending on data pipeline complexity and production model deployment requirements.'
        },
        {
            icon: cdnUrl('Saas-developement-company/api-development-third-party-integration.webp'),
            title: 'Third-Party Integrations',
            description:
                'Each enterprise system integration adds $5–25K depending on API quality, data model complexity, and bidirectional sync requirements. Integration scope is a commonly underestimated cost driver in SaaS projects.'
        },
        {
            icon: cdnUrl('Saas-developement-company/subscription-billing-complexity.svg'),
            title: 'Subscription Billing Complexity',
            description:
                'Simple flat-rate billing adds $5–15K. Usage-based metering, multi-tier pricing, and revenue recognition logic for enterprise contracts adds $25–60K. Billing bugs cost more than billing engineering.'
        },
        {
            icon: cdnUrl('Saas-developement-company/mobile-platform-requirements.svg'),
            title: 'Mobile Platform Requirements',
            description:
                'Native iOS + Android SaaS client adds 40–60% over web-only delivery. React Native or Flutter cross-platform reduces this to 20–30% while maintaining production-quality performance.'
        }
    ],
    buttonText: 'Get a Detailed Cost Estimate for Your Project'
};

export const saasWhoWeServeData = [
    {
        number: '01',
        heading: 'Startups — Validate Fast, Build Right',
        body: "Early-stage teams need speed and architectural control simultaneously. We build SaaS MVPs that are quick to launch, simple to manage, and built on the multi-tenant foundation that can support the Series A version of your product without a full re-architecture. Our SaaS MVP development service gets validated software into users' hands in 8–12 weeks — without the technical debt that kills SaaS products at growth stage."
    },
    {
        number: '02',
        heading: 'Scale-Ups — Grow Without Breaking',
        body: 'Growing SaaS companies hit architectural ceilings — the monolith that worked at 50 tenants breaks at 500. The billing system that handled flat-rate subscriptions cannot support usage-based pricing. Our SaaS platform re-architecture service helps growing companies standardize workflows, modernize tenancy models, improve performance visibility, and scale users and features without operational disruption.'
    },
    {
        number: '03',
        heading: 'Enterprises — Stability, Compliance, and Integration at Scale',
        body: "Large organizations require SaaS platforms that pass enterprise security reviews, integrate with complex existing systems, and operate under regulatory frameworks that cannot be treated as afterthoughts. Our enterprise SaaS development services deliver HIPAA-compliant, FedRAMP-aligned SaaS platforms with SSO federation, ERP integration, and SLA-backed operational infrastructure — built for the procurement requirements of the world's most demanding buyers."
    }
];

export const saasLeadersData = {
    items: [
        {
            title: 'Recognized Independently. Repeatedly. Without Paid Placements.',
            subheading:
                'Third-party validation across multiple years and platforms is rare. We have earned it consistently — because the recognition reflects delivery quality, not marketing investment. Every recognition below was earned through documented research methodologies with no paid placement.',
            awards: [
                {
                    year: '2020',
                    platform: 'GoodFirms × PR Newswire',
                    location: 'Canada',
                    category: 'Top Custom Software Development Companies',
                    description: '',
                    link: 'https://www.prnewswire.com/news-releases/goodfirms-discloses-the-filtered-list-of-best-custom-software-development-companies-for-q1--2020-301058542.html',
                    variant: 'light' as const
                },
                {
                    year: '2024',
                    platform: 'Clutch',
                    location: 'Clutch',
                    category: 'Clutch Top 1000 Global · 4.9⭐  · 280+ Verified Reviews',
                    description: '',
                    link: 'https://www.prnewswire.com/news-releases/goodfirms-discloses-the-filtered-list-of-best-custom-software-development-companies-for-q1--2020-301058542.html',
                    variant: 'light' as const
                },
                {
                    year: '2026',
                    isLatest: true,
                    platform: 'eSparkBiz Research',
                    location: 'Global',
                    category: 'Top AI Agent Development Company — Globally',
                    description: '',
                    link: 'https://www.esparkinfo.com/generative-ai/ai-agent/top-companies',
                    variant: 'dark' as const
                }
            ]
        },
        {
            title: 'No Handoffs Full-Stack SaaS Delivery — One Team, No Handoffs, No Gaps',
            subheading:
                'SaaS product development involves more moving parts than most software engagements — multi-tenant architecture, billing infrastructure, compliance validation, DevOps pipeline setup, <a href="/ai-development-company" target="_blank">AI integration</a>, and post-launch operational monitoring are not sequential phases you hand off between teams. They are concurrent engineering concerns that require shared architectural context throughout.<br/>Every SaaS engagement at Debut Infotech runs through one accountable team. No subcontractors. No knowledge handoffs. No ramp-up cost when new work is scoped. These are your SaaS app development providers for end-to-end solutions.',
            gridItems: [
                {
                    label: 'SaaS Architecture & Discovery',
                    iconKey: 'layers',
                    iconSrc: cdnImage('custom-software-developement-new/system-architecture-technical-discovery.webp').src
                },
                {
                    label: 'Multi-Tenant Database Design',
                    iconKey: 'storage',
                    iconSrc: cdnImage('custom-software-developement-new/backend-engineering-api-development.webp').src
                },
                {
                    label: 'Backend API Development',
                    iconKey: 'dashboard',
                    iconSrc: cdnImage('custom-software-developement-new/frontend-web-application-development.webp').src
                },
                {
                    label: 'Frontend & Web App Dev',
                    iconKey: 'phone',
                    iconSrc: cdnImage('custom-software-developement-new/mobile-application-development-ios-android.webp').src
                },
                {
                    label: 'AI/ML Integration',
                    iconKey: 'ai',
                    iconSrc: cdnImage('custom-software-developement-new/ai-ml-integration-intelligent-automation.webp').src
                },
                {
                    label: 'Subscription Billing Systems',
                    iconKey: 'cloud',
                    iconSrc: cdnImage('custom-software-developement-new/cloud-infrastructure-devops-cicd.webp').src
                },
                {
                    label: 'Cloud DevOps & CI/CD',
                    iconKey: 'qa',
                    iconSrc: cdnImage('custom-software-developement-new/qa-engineering-performance-testing.webp').src
                },
                {
                    label: 'QA & Security Audits',
                    iconKey: 'lock',
                    iconSrc: cdnImage('custom-software-developement-new/security-audits-compliance-implementation.webp').src
                },
                {
                    label: 'Post-Launch SaaS Support',
                    iconKey: 'settings',
                    iconSrc: cdnImage('custom-software-developement-new/post-launch-support-iterative-optimization.webp').src
                }
            ]
        },
        {
            title: ' Long-Term A SaaS Partner Through the Full Product Lifecycle — Not Just the Build',
            subheading:
                'The most expensive mistake in SaaS product development is not a bad feature decision — it is a good product built on an architecture that cannot support the version of the business that appears twelve months after launch. We structure every SaaS engagement for long-term architectural continuity: maintaining shared context on your platform, providing proactive technical guidance, and ensuring your SaaS platform can absorb growth without accumulating the technical debt that forces a full re-architecture.',
            tags: ['SLA-Backed Monitoring', 'Proactive Architecture Reviews', 'Compliance Framework Updates']
        }
    ]
};

export const saasDifferentiatorData = [
    {
        image: cdnUrl('Saas-developement-company/2011.webp'),
        title: "Pre-Cloud Era Engineers Who've Built Through Every Architectural Shift",
        description:
            'Building SaaS since 2011 — before multi-cloud was standard, before containers replaced bare-metal, and before AI became an expected product feature. Three architectural generations: monolith deployments, microservices decomposition, and AI-native SaaS infrastructure. A SaaS development agency assembled recently cannot sell you this.'
    },
    {
        image: cdnUrl('Saas-developement-company/Enterprise-Engagement.webp'),
        title: 'Regulated SaaS Delivery — Enterprise & Mid-Market',
        description:
            'SaaS platforms built for regulated industries are fundamentally different from general B2B software. HIPAA-compliant SaaS requires audit logging at the data access layer, not the application layer. SOC 2-ready SaaS controls embedded in the CI/CD pipeline, not documented after the fact. Our engineers carry working knowledge of the compliance frameworks that govern your industry.'
    },
    {
        image: cdnUrl('Saas-developement-company/Fabdep.webp'),
        title: 'Creators of FabDep (Fabric Deployer)',
        description:
            "We don't just use frameworks — we build them. FabDep is our internal deployment framework that simplifies Hyperledger Fabric network setup and reduces enterprise-grade infrastructure provisioning complexity. Tools built for internal use reflect the engineering discipline applied to every client engagement."
    }
];

export const saasFAQData = [
    {
        category: 'SaaS Development',
        questions: [
            {
                q: 'What is SaaS development?',
                a: 'SaaS development is the process of designing, building, and deploying cloud-hosted software applications accessed by users over the internet on a subscription basis. It involves multi-tenant architecture, subscription billing infrastructure, compliance architecture, and operational observability — built to serve hundreds of customer organizations from a single deployed codebase. A custom SaaS development company like Debut Infotech builds platforms that fit your exact workflows, data models, and compliance requirements, giving you full IP ownership and no vendor platform dependency.'
            },
            {
                q: 'How much does SaaS development cost?',
                a: 'SaaS development typically costs $30,000–$60,000 for a focused MVP and $150,000–$500,000 for a full enterprise SaaS platform. The three primary SaaS development cost drivers are: multi-tenancy architecture complexity, compliance requirements (HIPAA, SOC 2, PCI-DSS add 20–40%), and AI/<a href="/machine-learning-development-company" target="_blank">ML</a> integration depth. The most accurate cost estimate comes from a free scoping workshop with our solutions architects — which Debut Infotech provides before any engagement is signed.'
            },
            {
                q: 'How long does SaaS application development take?',
                a: 'SaaS MVP development typically takes 8–12 weeks. A full enterprise SaaS platform takes 5–10 months. Timeline is primarily driven by multi-tenancy architecture complexity, compliance validation cycles (security audits cannot be compressed without reducing scope), integration scope, and AI feature depth. Our agile SaaS development process delivers working, reviewable software every 2 weeks — not only at the end of the project.'
            },
            {
                q: 'What is the SaaS product development process?',
                a: 'A professional SaaS product development process covers 8 phases: Requirements Discovery, Project Planning, SaaS Architecture and UX Design, MVP Development, Full Development and QA, Production Deployment, Warranty Support, and Long-Term Engineering Partnership. Each phase has named owners, concrete deliverables, and formal sign-off gates. The architecture phase — where multi-tenancy model, billing system, and compliance scope are decided — is the highest-leverage point in the entire SaaS development lifecycle.'
            },
            {
                q: 'What is the difference between custom SaaS development and off-the-shelf SaaS?',
                a: "Off-the-shelf SaaS forces your business processes to conform to the tool's logic — and charges you compounding subscription fees indefinitely. Custom SaaS development builds the platform around your exact workflows, compliance obligations, and integration needs — giving you full IP ownership, no recurring licensing fees, and the ability to evolve the system as your business changes. The upfront investment in custom SaaS development typically pays back within 18–36 months for organizations paying $50K+/year in SaaS tools that only partially fit their needs."
            },
            {
                q: 'Do you offer SaaS development outsourcing?',
                a: 'Yes. Debut Infotech provides full SaaS development outsourcing — including dedicated development teams, staff augmentation for SaaS skill gaps, and complete project delivery for organizations without in-house SaaS engineering capacity. Our SaaS outsourcing model includes NDA and full IP transfer from Day 1, weekly velocity reporting, and team scaling with 2-week notice. We also provide nearshore SaaS development for US and Canadian clients requiring timezone overlap.'
            },
            {
                q: 'How do you handle multi-tenant SaaS architecture?',
                a: 'Multi-tenancy is treated as a base architectural requirement — not a later refactor. During the architecture phase, we evaluate three models: shared-schema (single database, row-level tenant isolation), isolated-schema (separate schemas per tenant), and siloed tenancy (fully isolated infrastructure per tenant). Each decision is documented in an Architecture Decision Record with the business rationale, performance implications, compliance considerations, and cost model. The wrong tenancy decision is one of the most expensive architectural mistakes in SaaS — we resolve it before development begins.'
            },
            {
                q: 'Can you build AI-integrated SaaS products?',
                a: 'Yes — AI integration is a standard component of modern SaaS architecture at Debut Infotech. We integrate LLMs (OpenAI, Anthropic Claude), build custom <a href="/blog/supervised-vs-unsupervised-learning-in-ml-models" target="_blank">ML models</a> for SaaS-specific prediction tasks (churn, demand forecasting, fraud detection), implement NLP-powered workflow automation, and design AI-ready data architectures from the first architecture session. AI in SaaS development is not a feature we bolt on after deployment — it is an architectural consideration from Week 1.'
            },
            {
                q: 'What is nearshore SaaS development?',
                a: "Nearshore SaaS development refers to outsourcing SaaS engineering to a team in a compatible time zone — enabling North American companies to work with engineers in India with dedicated timezone-overlap hours, async-first communication, and dedicated SaaS development teams that operate as an extension of the client's product organization. Debut Infotech provides nearshore SaaS development for US and Canadian clients with timezone-aligned working windows, daily standups, and weekly sprint reviews."
            },
            {
                q: 'Who owns the IP in a SaaS development engagement?',
                a: 'You do — 100%. All source code, architecture documentation, database schemas, and intellectual property created during a Debut Infotech SaaS development engagement transfers to you at delivery. This is a contractual guarantee in every engagement agreement. We do not retain licensing rights to code we write for SaaS clients, and we do not reuse client-specific architecture patterns for other engagements in the same market vertical.'
            },
            {
                q: 'What is the best saas development company for startups?',
                a: 'The best SaaS development company for startups is one that builds production-quality SaaS MVP architecture from sprint one — so you do not face a full re-architecture when you reach growth stage. Debut Infotech delivers SaaS MVP development in 8–12 weeks with multi-tenant architecture designed to scale, rated 4.9★ on Clutch across 280+ independently verified reviews, and recognized as a top SaaS developer by GoodFirms without paid placement. Our minimum project size is $10,000+ with hourly rates of $25–$49/hr.'
            },
            {
                q: 'How is SaaS product development different from SaaS platform development?',
                a: 'SaaS product development refers to building a specific software product — with a defined user persona, feature set, and go-to-market — from MVP through growth stage. SaaS platform development refers to building underlying infrastructure designed to support multiple products, customer tiers, or white-label deployments — typically involving multi-tenant architecture, partner APIs, and marketplace or reseller capabilities. Debut Infotech delivers both, and the architecture decisions differ significantly between the two engagement types.'
            }
        ]
    }
];

export const saasComparisonData = {
    eyebrow: 'Custom vs. Off-the-Shelf Software',
    heading: 'Custom Software Development vs. SaaS Tools vs. Open Source — Full Comparison',
    subheading:
        'Choosing the right software delivery model determines your competitive ceiling for the next 5 years. This comparison reflects real execution factors — not marketing claims.',
    col1Label: 'Factor',
    col3Label: 'Off-the-Shelf SaaS',
    col4Label: 'Open Source',
    col5Label: 'Low-Code Platforms',
    rows: [
        {
            criteria: 'Business Process Fit',
            debut: {type: 'check' as const, text: 'Exact Fit — built around your workflows'},
            generic: {type: 'warning' as const, text: 'Partial — you adapt to the tool'},
            offshore: {type: 'warning' as const, text: 'Configurable within limits'},
            inhouse: {type: 'cross' as const, text: 'Template-constrained'}
        },
        {
            criteria: 'Multi-Tenant Architecture',
            debut: {type: 'check' as const, text: 'Designed from sprint one'},
            generic: {type: 'warning' as const, text: 'Vendor-controlled model'},
            offshore: {type: 'warning' as const, text: 'DIY implementation required'},
            inhouse: {type: 'cross' as const, text: 'No architectural control'}
        },
        {
            criteria: 'Scalability',
            debut: {type: 'check' as const, text: 'Architected for your growth'},
            generic: {type: 'warning' as const, text: 'Vendor-controlled ceiling'},
            offshore: {type: 'warning' as const, text: 'Self-managed DevOps overhead'},
            inhouse: {type: 'cross' as const, text: 'Platform ceiling'}
        },
        {
            criteria: 'Data Sovereignty',
            debut: {type: 'check' as const, text: 'Full ownership — your infrastructure'},
            generic: {type: 'cross' as const, text: 'Vendor holds and controls data'},
            offshore: {type: 'check' as const, text: 'Self-hosted with full control'},
            inhouse: {type: 'cross' as const, text: 'Platform-hosted, terms can change'}
        },
        {
            criteria: 'AI & ML Integration',
            debut: {type: 'check' as const, text: 'Native, custom-trained — not plug-in'},
            generic: {type: 'warning' as const, text: 'Marketplace add-ons, limited scope'},
            offshore: {type: 'warning' as const, text: 'Manual build, no production support'},
            inhouse: {type: 'cross' as const, text: 'Not supported at architecture level'}
        },
        {
            criteria: 'Compliance (HIPAA/SOC2/GDPR)',
            debut: {type: 'check' as const, text: 'Architected in from sprint one'},
            generic: {type: 'warning' as const, text: 'Varies by vendor, often add-on cost'},
            offshore: {type: 'warning' as const, text: 'Self-configured, high burden'},
            inhouse: {type: 'cross' as const, text: 'Rarely supported for regulated use'}
        },
        {
            criteria: 'Integration Flexibility',
            debut: {type: 'check' as const, text: 'Unlimited — any system, any protocol'},
            generic: {type: 'warning' as const, text: 'Published APIs only'},
            offshore: {type: 'check' as const, text: 'Extensible with engineering effort'},
            inhouse: {type: 'cross' as const, text: 'Connector-limited'}
        },
        {
            criteria: 'Time to First Launch',
            debut: {type: 'warning' as const, text: '8–12 weeks (MVP)'},
            generic: {type: 'check' as const, text: 'Days to weeks'},
            offshore: {type: 'warning' as const, text: '3–9 months'},
            inhouse: {type: 'check' as const, text: 'Weeks (limited features)'}
        },
        {
            criteria: 'Long-Term Total Cost',
            debut: {type: 'check' as const, text: 'Lowest — no compounding SaaS fees'},
            generic: {type: 'cross' as const, text: 'Compounding subscription fees'},
            offshore: {type: 'warning' as const, text: 'High DevOps and maintenance cost'},
            inhouse: {type: 'cross' as const, text: 'Seat + usage fees + lock-in'}
        },
        {
            criteria: 'IP & Code Ownership',
            debut: {type: 'check' as const, text: '100% yours from day one'},
            generic: {type: 'cross' as const, text: 'Vendor-owned platform'},
            offshore: {type: 'warning' as const, text: 'License-dependent'},
            inhouse: {type: 'cross' as const, text: 'Platform-owned, no portability'}
        },
        {
            criteria: 'White-Label Capability',
            debut: {type: 'check' as const, text: 'Full white-label and multi-brand'},
            generic: {type: 'cross' as const, text: 'Vendor branding only'},
            offshore: {type: 'warning' as const, text: 'Possible with significant engineering'},
            inhouse: {type: 'cross' as const, text: 'Platform branding required'}
        }
    ],
    footerNotes: [
        'Custom SaaS development typically pays back within 18–36 months for organizations paying $50K+/year in SaaS subscriptions for tools that only partially fit their needs.'
    ]
};
